import { useEffect, useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { ActionIcon, Box, Button, Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconExclamationCircle,
  IconPencil,
  IconPhoto,
  IconRefresh,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import type { UploadQueueRow } from '@/db';
import { removeItem, retryError } from '@/lib/upload/queue';
import classes from './UploadItem.module.css';

interface UploadItemProps {
  item: UploadQueueRow;
  isLast?: boolean;
  /**
   * Called when an edit affordance is activated (before navigation) — used to
   * close the upload modal so the wallpaper edit page isn't left behind the
   * overlay. Optional: omitting it just navigates (safe for standalone use).
   */
  onNavigate?: (() => void) | undefined;
}

/**
 * Route to a wallpaper's edit page for a finished row, or `null` when the row is
 * not yet a persisted wallpaper (only `done` rows carry a `wallpaper_id`).
 */
function editHrefFor(item: UploadQueueRow): string | null {
  return item.status === 'done' && item.wallpaper_id != null
    ? `/wallpapers/${item.wallpaper_id}`
    : null;
}

interface BadgeStyle {
  label: string;
  bg: string;
  fg: string;
  icon: JSX.Element;
}

const BADGE: Record<UploadQueueRow['status'], BadgeStyle> = {
  done: {
    label: 'Ready',
    bg: '#e3efe7',
    fg: '#3f7d5b',
    icon: <IconCheck size={11} stroke={2} />,
  },
  uploading: {
    label: 'Uploading',
    bg: '#e2ecf5',
    fg: '#4a6b8a',
    icon: <IconUpload size={11} stroke={2} />,
  },
  processing: {
    label: 'Processing',
    bg: '#fdebd0',
    fg: '#a04000',
    icon: <IconRefresh size={11} stroke={2} />,
  },
  queued: {
    label: 'Queued',
    bg: '#f6f1ea',
    fg: '#807563',
    icon: <IconClock size={11} stroke={2} />,
  },
  paused: {
    label: 'Paused',
    bg: '#f6ecd6',
    fg: '#a87a1f',
    icon: <IconAlertTriangle size={11} stroke={2} />,
  },
  error: {
    label: 'Error',
    bg: '#f6dad5',
    fg: '#b03a2e',
    icon: <IconExclamationCircle size={11} stroke={2} />,
  },
};

function formatSize(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ status }: { status: UploadQueueRow['status'] }): JSX.Element {
  const m = BADGE[status];
  return (
    <Box
      component="span"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        background: m.bg,
        color: m.fg,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.2,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {m.icon}
      {m.label}
    </Box>
  );
}

function Thumb({ item }: { item: UploadQueueRow }): JSX.Element {
  const url = useMemo(() => {
    if (!item.file || item.status === 'error') return null;
    return URL.createObjectURL(item.file);
  }, [item.file, item.status]);

  useEffect(() => {
    if (!url) return;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [url]);

  const base = {
    width: 64,
    height: 48,
    borderRadius: 8,
    flexShrink: 0,
    overflow: 'hidden' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (item.status === 'error') {
    return (
      <Box style={{ ...base, background: '#f6dad5', color: '#b03a2e' }}>
        <IconExclamationCircle size={22} stroke={1.6} />
      </Box>
    );
  }
  if (!url) {
    return (
      <Box style={{ ...base, background: '#f6f1ea', color: '#aea38f' }}>
        <IconPhoto size={20} stroke={1.6} />
      </Box>
    );
  }
  return (
    <Box
      style={{
        ...base,
        background: `url(${url}) center/cover no-repeat`,
        position: 'relative',
      }}
    >
      {item.status === 'uploading' && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(31,26,20,0.35)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {item.progress}%
        </Box>
      )}
    </Box>
  );
}

export function UploadItem({ item, isLast = false, onNavigate }: UploadItemProps): JSX.Element {
  const isError = item.status === 'error';
  const editHref = editHrefFor(item);
  const editLabel = `Edit wallpaper ${item.fileName}`;

  const handleRetry = async (): Promise<void> => {
    await retryError(item.id);
  };
  const handleRemove = async (): Promise<void> => {
    await removeItem(item.id);
  };
  // Always a defined handler (no-op when no onNavigate) so it can go on onClick
  // under exactOptionalPropertyTypes without passing an explicit undefined.
  const handleNavigate = (): void => onNavigate?.();

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '64px 1fr auto',
        alignItems: 'center',
        gap: 14,
        padding: '10px 8px',
        borderBottom: isLast ? 'none' : '1px solid #ece4d8',
      }}
    >
      {editHref ? (
        <Box
          component={Link}
          to={editHref}
          onClick={handleNavigate}
          className={`${classes.thumbLink}`}
          aria-label={editLabel}
        >
          <Thumb item={item} />
        </Box>
      ) : (
        <Thumb item={item} />
      )}
      <Stack gap={3} style={{ minWidth: 0 }}>
        <Group gap={8} wrap="nowrap">
          {editHref ? (
            <Text
              component={Link}
              to={editHref}
              onClick={handleNavigate}
              className={`${classes.nameLink}`}
              aria-label={editLabel}
            >
              {item.fileName}
            </Text>
          ) : (
            <Text
              component="code"
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12.5,
                color: '#1f1a14',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {item.fileName}
            </Text>
          )}
          <StatusBadge status={item.status} />
        </Group>
        <Group gap={10} wrap="nowrap">
          <Text size="xs" c="#807563">
            {formatSize(item.fileSize)}
          </Text>
          {item.status === 'uploading' && (
            <Text size="xs" c="#807563">
              · {item.progress}%
            </Text>
          )}
        </Group>
        {isError && item.error && (
          <Group gap={6} wrap="nowrap" align="flex-start" style={{ marginTop: 1 }}>
            <Box
              style={{
                color: '#b03a2e',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                // Align icon center with the first line of text. xs text ≈ 12px
                // at line-height ~1.45 ⇒ ~17px line box. Center an 11px icon.
                height: 17,
              }}
            >
              <IconExclamationCircle size={11} stroke={2} />
            </Box>
            <Text size="xs" c="#b03a2e">
              {item.error}
            </Text>
          </Group>
        )}
        {(item.status === 'uploading' || item.status === 'processing') && (
          <Progress
            value={item.progress}
            size="xs"
            color={item.status === 'processing' ? '#a04000' : '#4a6b8a'}
            style={{ marginTop: 2, background: '#f6f1ea' }}
          />
        )}
      </Stack>
      <Group gap={4} wrap="nowrap">
        {isError && (
          <Button
            variant="default"
            size="compact-sm"
            leftSection={<IconRefresh size={11} />}
            onClick={() => void handleRetry()}
            style={{
              height: 28,
              borderRadius: 6,
              border: '1px solid #ece4d8',
              background: '#ffffff',
              color: '#4a4137',
              fontSize: 12,
              fontWeight: 500,
            }}
            aria-label={`Retry upload of ${item.fileName}`}
          >
            Retry
          </Button>
        )}
        {editHref && (
          <Button
            component={Link}
            to={editHref}
            onClick={handleNavigate}
            variant="default"
            size="compact-sm"
            leftSection={<IconPencil size={11} />}
            style={{
              height: 28,
              borderRadius: 6,
              border: '1px solid #ece4d8',
              background: '#ffffff',
              color: '#4a4137',
              fontSize: 12,
              fontWeight: 500,
            }}
            aria-label={editLabel}
          >
            Edit
          </Button>
        )}
        <Tooltip label="Remove from queue" position="top" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={28}
            onClick={() => void handleRemove()}
            aria-label={`Remove ${item.fileName} from queue`}
            style={{ borderRadius: 6, border: '1px solid #ece4d8', color: '#807563' }}
          >
            <IconTrash size={12} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Box>
  );
}
